import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Application,
  CreateApplication,
  CreateTemplate,
  Template,
  UpdateTemplate
} from '@thro-types/notification';

import { AxiosRequestConfig } from 'axios';

import {
  createApplication,
  getApplications,
  getTemplate,
  createTemplate,
  updateTemplate,
  getBulkTemplates,
  getTemplates
} from 'api/notificationService';

/**
 * A custom hook for all tiles related things from the UsersService.
 *
 * @returns DefinedUseQueryResult<Tile[], Error>
 */
export const useApplicationsQuery = (config?: AxiosRequestConfig) => {
  const query = useQuery({
    queryKey: ['applications'],
    queryFn: () => {
      const applications = getApplications(config);
      return applications;
    },
    initialData: []
  });

  return query;
};

export const useApplicationsMutation = (config?: AxiosRequestConfig) => {
  const queryClient = useQueryClient();

  const queryKey = ['applications'];
  const creation = useMutation<Application, Error, CreateApplication>({
    mutationFn: async args => {
      return await createApplication(args, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });

  return { creation } as const;
};

export const useTemplatesQuery = (config?: AxiosRequestConfig) => {
  const query = useQuery<Template[], Error, Template[]>({
    queryKey: ['templates', config ? JSON.stringify(config.params) : ''],
    queryFn: () => {
      const template = getTemplates(config);
      return template;
    }
  });

  return query;
};

export const useBulkTemplatesQuery = (config?: AxiosRequestConfig) => {
  const query = useQuery({
    queryKey: ['templates', config ? JSON.stringify(config.params) : ''],
    queryFn: () => {
      const template = getBulkTemplates(config);
      return template;
    }
  });

  return query;
};

export const useTemplateQuery = (config?: AxiosRequestConfig) => {
  const query = useQuery<Template, Error, Template>({
    queryKey: ['templates', config ? JSON.stringify(config.params) : ''],
    queryFn: () => {
      const template = getTemplate(config);
      return template;
    }
  });

  return query;
};

export const useTemplatesMutation = (config?: AxiosRequestConfig) => {
  const queryClient = useQueryClient();

  const queryKey = ['templates'];
  const creation = useMutation<Template, Error, CreateTemplate>({
    mutationFn: async args => {
      return await createTemplate(args, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });
  const update = useMutation<Template, Error, { id: number; data: UpdateTemplate }>({
    mutationFn: async args => {
      return await updateTemplate(args.id, args.data, config);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: data => {}
  });

  return { creation, update } as const;
};
